import {
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnInit,
  ViewChild,
  ViewContainerRef,
  Type,
  Output,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ChatMessage } from '../chat-message.model';
import { TranslatePipe } from 'haba-core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface DynamicComponentInstance {
  formSubmitted?: EventEmitter<unknown>;
  close?: EventEmitter<void>;
}

@Component({
  selector: 'app-chat-message',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './chat-message.component.html',
  styleUrl: './chat-message.component.scss',
})
export class ChatMessageComponent implements OnInit, OnDestroy {
  @Input() message!: ChatMessage;
  @Input() isLoading: boolean = false;
  @Output() submitMessage = new EventEmitter<unknown>();
  @Output() closeComponent = new EventEmitter<void>();
  @ViewChild('dynamicComponentHost', { read: ViewContainerRef, static: true })
  dynamicComponentHost!: ViewContainerRef;

  private componentRef: ComponentRef<DynamicComponentInstance> | null = null;
  onDestroy$ = new Subject();

  constructor() {}

  ngOnInit(): void {
    this.loadComponent();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  get formattedContent(): string {
    if (typeof this.message.content !== 'string') return '';
    return this.message.content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br>');
  }

  contentIsComponent() {
    return this.isAngularComponent(this.message.content);
  }

  isAngularComponent(variable: unknown): boolean {
    let target = variable;

    if (typeof target !== 'function') {
      if (
        target &&
        typeof target === 'object' &&
        target.constructor &&
        typeof target.constructor === 'function'
      ) {
        target = target.constructor;
      } else {
        return false;
      }
    }

    // Cast to Record to safely check properties without 'any'
    if (Object.prototype.hasOwnProperty.call(target, 'ɵcmp')) {
      return true;
    }

    if (
      Object.prototype.hasOwnProperty.call(target, '__annotations__') &&
      Array.isArray((target as { __annotations__: unknown[] })['__annotations__'])
    ) {
      return true;
    }

    return false;
  }

  loadComponent() {
    this.dynamicComponentHost.clear();

    if (this.contentIsComponent()) {
      this.componentRef = this.dynamicComponentHost.createComponent(
        this.message.content as Type<DynamicComponentInstance>
      );

      if (this.componentRef.instance.formSubmitted instanceof EventEmitter) {
        this.componentRef.instance.formSubmitted
          .pipe(takeUntil(this.onDestroy$))
          .subscribe((formData: unknown) => {
            this.submitMessage.emit(formData);

            this.dynamicComponentHost.clear();
          });
      }

      if (this.componentRef.instance.close instanceof EventEmitter) {
        this.componentRef.instance.close.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
          this.closeComponent.emit();
        });
      }
    }
  }
}
